import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GitFork, MessagesSquare, Megaphone, Images, Gem, Vault as VaultIcon, LogOut, Settings, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fileUrl } from "../lib/api";
import { InviteSheet } from "./InviteSheet";

const tabs = [
  { to: "/lineage", label: "Lineage", icon: GitFork, testid: "sidebar-lineage-tab" },
  { to: "/hangouts", label: "Hangouts", icon: MessagesSquare, testid: "sidebar-hangouts-tab" },
  { to: "/reunions", label: "Reunions", icon: Megaphone, testid: "sidebar-reunions-tab" },
  { to: "/memories", label: "Memories", icon: Images, testid: "sidebar-memories-tab" },
  { to: "/vault", label: "The Vault", icon: Gem, testid: "sidebar-vault-tab" },
];

export function DesktopSidebar() {
  const { user, family, logout } = useAuth();
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <aside className="fv-sidebar flex-col glass" data-testid="desktop-sidebar"
      style={{ borderRight: "1px solid var(--border)" }}>
      <div className="flex flex-col h-full w-full px-4 py-6">
        {/* Brand */}
        <div className="flex items-center gap-3 px-2 mb-8">
          <div className="h-10 w-10 rounded-xl grid place-items-center gold-border shrink-0"
            style={{ background: "var(--bg-elevated)", boxShadow: "0 0 20px var(--shell-glow)" }}>
            <VaultIcon size={20} className="gold-text" />
          </div>
          <div className="min-w-0">
            <div className="font-serif-display text-xl leading-none truncate">The Family Vault</div>
            {family?.name && (
              <div className="text-xs mt-1 truncate" style={{ color: "var(--text-secondary)" }} data-testid="sidebar-family-name">
                {family.name}
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1.5">
          {tabs.map((t) => (
            <NavLink key={t.to} to={t.to} data-testid={t.testid}>
              {({ isActive }) => (
                <motion.div
                  whileHover={{ x: 3 }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  className="flex items-center gap-3 rounded-xl px-3.5 py-3 relative"
                  style={isActive
                    ? { background: "rgba(124,131,255,0.12)", border: "1px solid rgba(124,131,255,0.28)" }
                    : { border: "1px solid transparent" }}
                >
                  <t.icon size={19} strokeWidth={isActive ? 2.2 : 1.8}
                    style={{ color: isActive ? "var(--accent)" : "var(--text-secondary)" }} />
                  <span className="text-sm font-medium"
                    style={{ color: isActive ? "var(--accent)" : "var(--text-secondary)" }}>
                    {t.label}
                  </span>
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Profile + quick actions + logout */}
        <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--hairline)" }}>
          <button onClick={() => navigate("/profile")} data-testid="sidebar-profile-btn"
            className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-[var(--overlay)]">
            <div className="h-10 w-10 rounded-full grid place-items-center gold-border overflow-hidden shrink-0"
              style={{ background: "var(--bg-elevated)" }}>
              {user?.photo ? (
                <img src={fileUrl(user.photo)} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="font-serif-display gold-text">{(user?.name || "?").charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] capitalize truncate" style={{ color: "var(--text-muted)" }}>
                {family?.role || "member"}
              </div>
            </div>
          </button>

          <button onClick={() => setInviteOpen(true)} data-testid="sidebar-invite-btn"
            className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 mt-1 transition-colors hover:bg-[var(--overlay)]"
            style={{ color: "var(--text-secondary)" }}>
            <UserPlus size={17} className="gold-text" />
            <span className="text-sm">Invite family</span>
          </button>
          <button onClick={() => navigate("/settings")} data-testid="sidebar-settings-btn"
            className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 transition-colors hover:bg-[var(--overlay)]"
            style={{ color: "var(--text-secondary)" }}>
            <Settings size={17} className="gold-text" />
            <span className="text-sm">Settings</span>
          </button>
          <button onClick={logout} data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 transition-colors hover:bg-[var(--overlay)]"
            style={{ color: "var(--text-muted)" }}>
            <LogOut size={17} />
            <span className="text-sm">Log out</span>
          </button>
        </div>
      </div>
      <InviteSheet open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </aside>
  );
}
